import { block, ellipse, rect, shadow, type Ctx } from './paint';

type Facing = 'up' | 'down' | 'left' | 'right';

/** Wheel with a turning tread mark. */
function wheel(c: Ctx, x: number, y: number, r: number, phase: number, side: boolean): void {
  if (side) {
    ellipse(c, '#1b1d24', x, y, r, r);
    ellipse(c, '#9a9ea6', x, y, r * 0.45, r * 0.45);
    const a = phase * Math.PI * 4;
    rect(c, '#d8dce2', x + Math.cos(a) * r * 0.7 - 0.4, y + Math.sin(a) * r * 0.7 - 0.4, 0.8, 0.8);
  } else {
    block(c, x - 1.3, y - r, 2.6, r * 2, '#1b1d24');
    const yy = y - r + ((phase * 3) % 1) * r * 2;
    rect(c, 'rgba(160,165,170,0.7)', x - 0.8, yy, 1.6, 0.5);
  }
}

/** A kick scooter: deck, T-bar, two small wheels. The rider stands on it. */
export function drawKick(c: Ctx, x: number, y: number, facing: Facing, phase: number, t: number): void {
  const side = facing === 'left' || facing === 'right';
  shadow(c, x, y + 1, side ? 10 : 4, side ? 2 : 3.2);
  const wob = Math.sin(t * 6) * 0.3;
  if (side) {
    const d = facing === 'right' ? 1 : -1;
    // Deck and stem.
    rect(c, '#2a2a30', x - 7 * d - (d < 0 ? 0 : 0), y - 3, 14, 1.6);
    rect(c, '#3fa36f', x - 6 * d, y - 3.2 + wob, 11, 1);
    rect(c, '#9a9ea6', x + 7 * d - 0.6, y - 16, 1.2, 13.5);
    rect(c, '#2a2a30', x + 4 * d, y - 16.6, 6, 1.4);
    wheel(c, x - 7 * d, y - 1, 2.2, phase, true);
    wheel(c, x + 7 * d, y - 1, 2.2, phase, true);
    return;
  }
  // Front / back view: narrow deck, the handlebar wide above.
  const front = facing === 'down';
  rect(c, '#9a9ea6', x - 0.6, y - 16, 1.2, 13);
  rect(c, '#2a2a30', x - 6, y - 16.8, 12, 1.5);
  block(c, x - 7.5, y - 17.4, 3, 2.2, '#2a2a30');
  block(c, x + 4.5, y - 17.4, 3, 2.2, '#2a2a30');
  rect(c, '#3fa36f', x - 2.2, y - 3.5, 4.4, 2.2);
  wheel(c, x, y + (front ? 0.5 : -0.5), 2.4, phase, false);
}

/** A small electric scooter (e-bike): body, seat, mirrors, headlight. The rider sits on it. */
export function drawEbike(c: Ctx, x: number, y: number, facing: Facing, phase: number, t: number): void {
  const side = facing === 'left' || facing === 'right';
  shadow(c, x, y + 2, side ? 14 : 6, side ? 2.6 : 4.4, 0.32);
  const body = '#e2493b', dark = '#a8261c', light = '#ff7a6a';
  const hum = Math.sin(t * 20) * 0.15;
  if (side) {
    const d = facing === 'right' ? 1 : -1;
    wheel(c, x - 9 * d, y, 3.4, phase, true);
    wheel(c, x + 9 * d, y, 3.4, phase, true);
    // Floorboard, body and the front shield.
    rect(c, '#2a2a30', x - 7 * d - (d < 0 ? 0 : 0), y - 3 + hum, 14, 1.6);
    block(c, x - 6 * d - (d < 0 ? 4 : 0), y - 11 + hum, 10, 7, body, light, dark);
    c.fillStyle = body;
    c.beginPath();
    c.moveTo(x + 5 * d, y - 3 + hum);
    c.lineTo(x + 9 * d, y - 15 + hum);
    c.lineTo(x + 11 * d, y - 15 + hum);
    c.lineTo(x + 8 * d, y - 3 + hum);
    c.fill();
    // Seat, handlebar, mirror, headlight.
    block(c, x - 9 * d - (d < 0 ? 0 : 7), y - 13 + hum, 7, 2.6, '#1b1d24');
    rect(c, '#9a9ea6', x + 9 * d - 0.5, y - 20 + hum, 1, 5);
    rect(c, '#2a2a30', x + 6 * d - (d < 0 ? 0 : 0), y - 20.6 + hum, 5, 1.4);
    ellipse(c, '#9fd3f0', x + 10.5 * d, y - 21.5 + hum, 1.2, 1);
    ellipse(c, '#fff6c0', x + 11 * d, y - 13 + hum, 1.4, 1.4);
    return;
  }
  const front = facing === 'down';
  wheel(c, x, y + (front ? 3 : -3), 3.6, phase, false);
  // Body seen from the front/back: shield, light, mirrors, seat.
  block(c, x - 6, y - 14 + hum, 12, 11, body, light, dark);
  if (front) {
    ellipse(c, '#fff6c0', x, y - 9 + hum, 2.6, 1.8);
    rect(c, '#2a2a30', x - 9, y - 19 + hum, 18, 1.5);
    ellipse(c, '#9fd3f0', x - 9.5, y - 20.5 + hum, 1.3, 1.1);
    ellipse(c, '#9fd3f0', x + 9.5, y - 20.5 + hum, 1.3, 1.1);
  } else {
    block(c, x - 5, y - 11 + hum, 10, 3, '#1b1d24');
    rect(c, '#ff3a2a', x - 2.5, y - 7 + hum, 5, 1.6);
    rect(c, '#2a2a30', x - 9, y - 19 + hum, 18, 1.5);
  }
}
